import React, { useEffect, useState } from 'react';
import {
  Box,
  Select,
  MenuItem,
  Stack,
  InputLabel,
  Typography,
} from '@mui/material';
import { DateTime, Settings } from 'luxon';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';

// MESSAGES
import DateRangeFormattedMessages from '../../messages/DateRangeFormattedMessages';

const DateRangeSelector = ({
  rangeOnly,
  handleDateSearch,
  rangeFunction,
  setParentStartDate,
  setParentEndDate,
  scheduledDates,
}) => {
  Settings.defaultZone = 'system';

  let timeRange = localStorage.getItem('timeRange') || 7;

  if (scheduledDates && timeRange > 0) {
    timeRange = -1 * timeRange;
  } else if (!scheduledDates && timeRange < 0) {
    timeRange = -1 * timeRange;
  }

  const [searchValue, setSearchValue] = useState(timeRange);
  const [displayStart, setDisplayStart] = useState(
    localStorage.getItem('timeRangeStart') !== 'null'
      ? DateTime.fromISO(localStorage.getItem('timeRangeStart'))
      : null
  );
  const [displayEnd, setDisplayEnd] = useState(
    localStorage.getItem('timeRangeEnd') !== 'null'
      ? DateTime.fromISO(localStorage.getItem('timeRangeEnd'))
      : null
  );
  const [timezoneString, setTimezoneString] = useState(
    "Fetching User's Timezone..."
  );

  console.log('timeRange', timeRange);

  useEffect(() => {
    handleDateChange();
  }, []);

  useEffect(() => {
    setParentStartDate(displayStart);
    setParentEndDate(displayEnd);
  }, [displayStart, displayEnd]);

  useEffect(() => {
    const start = localStorage.getItem('timeRangeStart');
    const end = localStorage.getItem('timeRangeEnd');

    setDisplayStart(start !== 'null' ? DateTime.fromISO(start) : null);
    setDisplayEnd(end !== 'null' ? DateTime.fromISO(end) : null);
    initialiseDates();
  }, []);

  const initialiseDates = () => {
    Settings.defaultZone = 'system';
    setTimezoneString(
      `User's Timezone is ${localStorage.getItem('timeString')}`
    );
    setDates(searchValue);
    searchBetweenDates();
  };

  const setDates = (value) => {
    localStorage.setItem('timeRange', value);
    Settings.defaultZone = localStorage.getItem('timeShort');

    let current = DateTime.now();
    let startDate, endDate;

    if (value === '24 hrs') {
      endDate = current;
      startDate = endDate.minus({ hours: 24 });
    } else if (value === 'custom') {
      startDate = displayStart ?? current.startOf('day').minus({ days: 6 });
      endDate = displayEnd ?? current.endOf('day');
    } else {
      // If scheduled page, go forwards.
      if (scheduledDates) {
        endDate = current.endOf('day').minus({ days: value + 1 });
        startDate = current.startOf('day');
      } else {
        endDate = current.endOf('day');
        startDate = current.startOf('day').minus({ days: value - 1 });
      }
    }

    setDisplayStart(startDate);
    setDisplayEnd(endDate);
    setSearchValue(value);
  };

  const searchBetweenDates = async () => {
    if (!displayStart || !displayEnd) return;
    await handleDateSearch(
      displayStart.toMillis(),
      displayEnd.toMillis(),
      searchValue
    );
  };

  const handleDateChange = (value, isStartDate) => {
    if (!value) return;
    localStorage.setItem('timeRange', 'custom');
    setSearchValue('custom');
    if (isStartDate) {
      setDisplayStart(value);
      localStorage.setItem('timeRangeStart', value.toISO());
    } else {
      setDisplayEnd(value);
      localStorage.setItem('timeRangeEnd', value.toISO());
    }
  };

  const getDateRangeOptions = () => {
    if (scheduledDates) {
      return [
        <MenuItem key={1} value={-1}>
          {DateRangeFormattedMessages.todayText}
        </MenuItem>,
        <MenuItem key={7} value={-7}>
          {DateRangeFormattedMessages.nextWeekText}
        </MenuItem>,
        <MenuItem key={14} value={-14}>
          {DateRangeFormattedMessages.nextFortnightText}
        </MenuItem>,
        <MenuItem key={30} value={-30}>
          {DateRangeFormattedMessages.nextMonthText}
        </MenuItem>,
        <MenuItem key={90} value={-90}>
          {DateRangeFormattedMessages.nextThreeMonthText}
        </MenuItem>,
        <MenuItem key={365} value={-365}>
          {DateRangeFormattedMessages.nextYearText}
        </MenuItem>,
        <MenuItem key={'custom'} value="custom">
          {DateRangeFormattedMessages.customText}
        </MenuItem>,
      ];
    }

    return [
      <MenuItem key={1} value={1}>
        {DateRangeFormattedMessages.todayText}
      </MenuItem>,
      <MenuItem key={'24 hrs'} value={'24 hrs'} selected>
        {DateRangeFormattedMessages.hoursText}
      </MenuItem>,
      <MenuItem key={2} value={2} selected>
        {DateRangeFormattedMessages.yesterdayText}
      </MenuItem>,
      <MenuItem key={7} value={7} selected>
        {DateRangeFormattedMessages.weekText}
      </MenuItem>,
      <MenuItem key={114} value={14}>
        {DateRangeFormattedMessages.fortnightText}
      </MenuItem>,
      <MenuItem key={30} value={30}>
        {DateRangeFormattedMessages.monthText}
      </MenuItem>,
      <MenuItem key={90} value={90}>
        {DateRangeFormattedMessages.threeMonthText}
      </MenuItem>,
      <MenuItem key={365} value={365}>
        {DateRangeFormattedMessages.yearText}
      </MenuItem>,
      <MenuItem key={'custom'} value="custom">
        {DateRangeFormattedMessages.customText}
      </MenuItem>,
    ];
  };

  return (
    <LocalizationProvider dateAdapter={AdapterLuxon}>
      <Stack sx={{ gap: 2 }}>
        {!rangeOnly && (
          <Stack
            sx={{ flexDirection: { sm: 'row' }, alignItems: { sm: 'center' } }}
          >
            <Box sx={{ minWidth: 160 }}>
              <InputLabel id="preset-timeframe-select-label">
                {DateRangeFormattedMessages.quickReport}
              </InputLabel>
            </Box>

            <Select
              name="preset-timeframe-select"
              id="preset-timeframe-select"
              labelId="preset-timeframe-select-label"
              value={searchValue}
              size="small"
              onChange={(e) => setDates(e.target.value)}
            >
              {getDateRangeOptions()}
            </Select>
          </Stack>
        )}

        <Box>
          <Stack
            sx={{
              flexDirection: { md: 'row' },
              alignItems: { md: 'center' },
              gap: { xs: 1, md: 0 },
              marginBottom: 1,
            }}
          >
            <Box sx={{ minWidth: 160 }}>
              <Typography>
                {DateRangeFormattedMessages.activityBetween}
              </Typography>
            </Box>

            <Stack
              sx={{
                flexDirection: { sm: 'row' },
                alignItems: { sm: 'center' },
                columnGap: 2,
                flex: 1,
              }}
            >
              <DateTimePicker
                value={displayStart}
                onChange={(value) => handleDateChange(value, true)}
                maxDate={DateTime.now()}
                format="dd/MMM/yyyy hh:mm a"
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />

              <Typography>To</Typography>

              <DateTimePicker
                value={displayEnd}
                onChange={(value) => handleDateChange(value, false)}
                format="dd/MMM/yyyy hh:mm a"
                slotProps={{
                  textField: {
                    size: 'small',
                  },
                }}
              />
            </Stack>
          </Stack>

          <Typography
            variant="span"
            fontSize={12}
            sx={{ marginLeft: { md: 20 }, display: 'block' }}
          >
            {timezoneString}
          </Typography>
        </Box>
      </Stack>
    </LocalizationProvider>
  );
};

export default DateRangeSelector;
